import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiService } from '../../services/api';
import './Success.css';

const MAX_POLLS = 5;
const POLL_INTERVAL_MS = 3000;

const Success = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const apiService = useApiService();

  const [orderId, setOrderId] = useState('');
  const [verifyState, setVerifyState] = useState('loading'); // loading | paid | unpaid | failed | error
  const [pollCount, setPollCount] = useState(0);

  const ordIdFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('order_id') || '';
  }, [location.search]);

  const verifyPayment = useCallback(async (id) => {
    try {
      const response = await apiService.translations.verifyPayment(id);
      const { payment_status, order_status } = response.data;

      if (payment_status === 'paid' || order_status === 'pending' || order_status === 'in_progress' || order_status === 'completed') {
        setVerifyState('paid');
      } else if (payment_status === 'unpaid') {
        setVerifyState('unpaid');
      } else {
        setVerifyState('failed');
      }
    } catch {
      setVerifyState('error');
    }
  }, [apiService.translations]);

  // On mount: extract order_id and start verification
  useEffect(() => {
    const id = ordIdFromUrl()
      || location.state?.orderDetails?.orderId
      || '';
    setOrderId(id);

    if (id) {
      verifyPayment(id);
    } else {
      setVerifyState('error');
    }
  }, [ordIdFromUrl, location.state, verifyPayment]);

  // If payment is still 'unpaid' after the first check, poll a few more times
  // (webhook may arrive a couple of seconds after the redirect)
  useEffect(() => {
    if (verifyState !== 'unpaid' || pollCount >= MAX_POLLS || !orderId) return;

    const timer = setTimeout(() => {
      setPollCount(c => c + 1);
      verifyPayment(orderId);
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [verifyState, pollCount, orderId, verifyPayment]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (verifyState === 'loading' || (verifyState === 'unpaid' && pollCount < MAX_POLLS)) {
    return (
      <div className="payment-success-page">
        <h1 className="payment-title">{t('payment.title')}</h1>
        <div className="success-card">
          <div className="success-content">
            <p className="success-info">
              {t('payment.processing') || 'Verifying your payment…'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (verifyState === 'failed') {
    return (
      <div className="payment-success-page">
        <h1 className="payment-title">{t('payment.cancelTitle')}</h1>
        <div className="success-card">
          <div className="success-content">
            <h2>{t('payment.orderCancelled')}</h2>
            <p className="success-info">{t('payment.cancelInfo')}</p>
            {orderId && (
              <div className="order-number">
                <p>{t('payment.orderNumber')}: {orderId}</p>
              </div>
            )}
            <Link to="/" className="home-btn">{t('payment.returnHome')}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (verifyState === 'error' || (verifyState === 'unpaid' && pollCount >= MAX_POLLS)) {
    return (
      <div className="payment-success-page">
        <h1 className="payment-title">{t('payment.title')}</h1>
        <div className="success-card">
          <div className="success-content">
            <h2>{t('payment.thankYou')}</h2>
            <p className="success-info">{t('payment.trackingInfo')}</p>
            {orderId && (
              <div className="order-number">
                <p>{t('payment.orderNumber')}: {orderId}</p>
              </div>
            )}
            <Link to={`/track-order?order_id=${orderId}`} className="track-btn">
              {t('payment.trackOrder')}
            </Link>
            <Link to="/" className="home-btn">{t('payment.returnHome')}</Link>
          </div>
        </div>
      </div>
    );
  }

  // paid
  return (
    <div className="payment-success-page">
      <h1 className="payment-title">{t('payment.title')}</h1>
      <div className="success-card">
        <div className="success-content">
          <h2>{t('payment.thankYou')}</h2>
          <p className="success-message">{t('payment.orderReceived')}</p>
          <p className="success-info">{t('payment.trackingInfo')}</p>
          {orderId && (
            <div className="order-number">
              <p>{t('payment.orderNumber')}: {orderId}</p>
            </div>
          )}
          <Link to={`/track-order?order_id=${orderId}`} className="track-btn">
            {t('payment.trackOrder')}
          </Link>
          <Link to="/" className="home-btn">{t('payment.returnHome')}</Link>
        </div>
      </div>
    </div>
  );
};

export default Success;
